import * as React from 'react';
import { Menu } from '@base-ui/react/menu';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

/*
 * How long the pointer rests on a tile before its pages appear, and how long
 * they linger after it leaves.
 *
 * The open delay is what stops a pointer travelling down the rail from flashing
 * a menu per tile on its way past. The close delay is the bridge over the gap
 * between the tile and the popup — leave it out and the popup vanishes halfway
 * through the reach for it.
 */
const OPEN_DELAY = 150;
const CLOSE_DELAY = 150;

interface RailFlyoutContextValue {
  /* The tile, so the popup lands in the same place whichever half opened it. */
  anchorRef: React.RefObject<HTMLDivElement | null>;
  hoverOpen: (event: React.PointerEvent) => void;
  hoverClose: () => void;
  hoverKeep: () => void;
}

const RailFlyoutContext = React.createContext<RailFlyoutContextValue | undefined>(
  undefined,
);

function useRailFlyoutContext(): RailFlyoutContextValue {
  const context = React.useContext(RailFlyoutContext);
  if (!context) {
    throw new Error(
      'Wundo UI: the RailFlyout parts must be rendered inside a <RailFlyout>.',
    );
  }
  return context;
}

export interface RailFlyoutProps {
  open: boolean;
  /*
   * Controlled from the rail rather than held here: whichever tile opens closes
   * the one before it, so two popups are never on screen at once.
   */
  onOpenChange: (open: boolean) => void;
  openDelay?: number;
  children: React.ReactNode;
}

/*
 * A rail tile's pages, in a popup beside it.
 *
 * Hover is handled here rather than by Base UI's own `openOnHover`, which lives
 * on the trigger: the trigger is the arrow, and the whole tile has to be the
 * hover target — a mouse should not have to find a 16px arrow to see where a
 * group leads.
 */
export function RailFlyout({
  open,
  onOpenChange,
  openDelay = OPEN_DELAY,
  children,
}: RailFlyoutProps) {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /*
   * Only a popup the pointer opened closes when the pointer leaves. One opened
   * from the arrow stays until Escape, a choice, or a click outside — otherwise
   * a keyboard user's menu disappears the moment the mouse drifts.
   */
  const byHover = React.useRef(false);

  React.useEffect(() => () => clearTimeout(timer.current), []);

  const value = React.useMemo<RailFlyoutContextValue>(() => {
    const cancel = () => {
      clearTimeout(timer.current);
      timer.current = undefined;
    };

    return {
      anchorRef,
      hoverOpen(event) {
        /* A finger has no hover, and a tap on the tile is a navigation. */
        if (event.pointerType !== 'mouse') return;
        cancel();
        timer.current = setTimeout(() => {
          byHover.current = true;
          onOpenChange(true);
        }, openDelay);
      },
      hoverClose() {
        cancel();
        if (!byHover.current) return;
        timer.current = setTimeout(() => {
          byHover.current = false;
          onOpenChange(false);
        }, CLOSE_DELAY);
      },
      hoverKeep: cancel,
    };
  }, [onOpenChange, openDelay]);

  return (
    <RailFlyoutContext.Provider value={value}>
      <Menu.Root
        open={open}
        onOpenChange={(next, details) => {
          /*
           * Base UI reports only what it handled itself — the arrow, Escape, a
           * choice, a click outside. None of those is the pointer resting on the
           * tile, so from here on the pointer leaving must not close it.
           */
          const hoverOpened = byHover.current;
          byHover.current = false;
          clearTimeout(timer.current);
          /*
           * The pointer crosses the tile on its way to the arrow, so the hover
           * has often opened the popup by the time the arrow is clicked. That
           * click means "open", not "close": it only pins the popup.
           */
          if (!next && hoverOpened && details.reason === 'trigger-press') return;
          onOpenChange(next);
        }}
        /*
         * Never modal: the popup opens on hover, and a hover that locks page
         * scroll and swallows clicks on the page behind it is a trap.
         */
        modal={false}
      >
        {children}
      </Menu.Root>
    </RailFlyoutContext.Provider>
  );
}

export type RailFlyoutTileProps = Omit<React.ComponentProps<'div'>, 'style'>;

/* The tile itself: the hover target, and what the popup is anchored to. */
export function RailFlyoutTile({ className, ...props }: RailFlyoutTileProps) {
  const { anchorRef, hoverOpen, hoverClose } = useRailFlyoutContext();

  return (
    <div
      ref={anchorRef}
      data-slot='rail-flyout-tile'
      className={cn('relative', className)}
      onPointerEnter={hoverOpen}
      onPointerLeave={hoverClose}
      {...props}
    />
  );
}

export interface RailFlyoutTriggerProps extends Omit<
  Menu.Trigger.Props,
  'className' | 'style'
> {
  className?: string;
}

/*
 * The way in without a mouse: the arrow opens the same popup on Enter or a tap.
 *
 * It sits on the tile rather than beside it, so the tile keeps the 52px row the
 * rail's rhythm is built on. Against the right edge at the icon's height, clear
 * of the label underneath — which runs the full width of the tile.
 */
export function RailFlyoutTrigger({
  className,
  children,
  ...props
}: RailFlyoutTriggerProps) {
  return (
    <Menu.Trigger
      data-slot='rail-flyout-trigger'
      className={cn(
        'absolute top-4 right-0 flex size-4 -translate-y-1/2 cursor-pointer items-center justify-center',
        'rounded-xs text-ink-muted transition-colors hover:text-ink data-popup-open:text-ink',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        className,
      )}
      {...props}
    >
      {children ?? <ChevronRight className='size-3.5' aria-hidden='true' />}
    </Menu.Trigger>
  );
}

export interface RailFlyoutContentProps extends Omit<
  Menu.Popup.Props,
  'className' | 'style'
> {
  className?: string;
}

/*
 * Beside the tile and aligned with its top edge, so the popup reads as opening
 * out of the row it belongs to. It caps itself at the room the viewport leaves
 * and scrolls: Admin holds seventeen tools in five runs, which is taller than a
 * laptop screen.
 */
export function RailFlyoutContent({
  className,
  children,
  ...props
}: RailFlyoutContentProps) {
  const { anchorRef, hoverKeep, hoverClose } = useRailFlyoutContext();

  return (
    <Menu.Portal>
      <Menu.Positioner
        anchor={anchorRef}
        side='right'
        align='start'
        sideOffset={10}
        className='z-50'
      >
        <Menu.Popup
          data-slot='rail-flyout-content'
          /* The reach from the tile crosses the rail's edge; the popup holds itself open. */
          onPointerEnter={hoverKeep}
          onPointerLeave={hoverClose}
          className={cn(
            'max-h-(--available-height) min-w-48 overflow-y-auto rounded-md border border-line',
            'bg-surface p-1.5 shadow-menu outline-none animate-pop',
            className,
          )}
          {...props}
        >
          {children}
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  );
}

export const railFlyoutItemVariants = cva(
  [
    'flex w-full cursor-pointer items-center rounded-sm px-2.75 py-2 text-left text-sm',
    'transition-colors outline-none',
  ],
  {
    variants: {
      /* The page you are on, marked so the popup says where you already are. */
      active: {
        true: 'bg-fill font-semibold text-ink',
        false: [
          'font-medium text-ink-secondary',
          'hover:bg-fill hover:text-ink data-highlighted:bg-fill data-highlighted:text-ink',
        ],
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type RailFlyoutItemVariants = VariantProps<typeof railFlyoutItemVariants>;

export interface RailFlyoutItemProps
  extends Omit<Menu.Item.Props, 'className' | 'style'>, RailFlyoutItemVariants {
  className?: string;
}

/* A page in the popup. Given a link to `render`, it is that link. */
export function RailFlyoutItem({ className, active, ...props }: RailFlyoutItemProps) {
  return (
    <Menu.Item
      data-slot='rail-flyout-item'
      className={cn(railFlyoutItemVariants({ active }), className)}
      {...props}
    />
  );
}
